<script setup>
// FLOW-06 (SPEC S4, a lista; Task 6.0, depois 6.6) — a seção Execuções da aba PowerFlow: toda
// execução de produção da conta, de qualquer origem, das mais novas para as mais antigas. Recebe a
// página do `GET /api/v1/accounts/:id/powerflow/execucoes` como veio (`{execucoes, proximo}`, o
// contrato entre as trilhas) e só mostra: quem a usa busca a página, filtra no servidor e abre o
// detalhe (Task 6.7). A atualização ao vivo é da Task 6.6.
//
// **Cada linha** diz o estado (glifo colorido, texto neutro), o flow e a versão que rodou, quem chamou
// com nome (PLAN G24: "Agente Sayuri", "Ana · manual", "API · Ana", "MCP · Claude de Ana"), quando
// começou e quanto durou; na falha, o passo onde parou e a mensagem de leigo (D19). A execução que
// voltou depois de uma queda leva a marca "retomada" (S1, `retomadas`). O código do desfecho e a
// ligação com a execução de origem ficam no detalhe. Quem chama é o agente (o PowerAgents ou o
// agent-runner antigo, `runner`), a aba, a API, o MCP e, desde a FLOW-03a, a agenda e o webhook,
// pelo `id` do gatilho ("Agenda · varredura"). O evento do OS é da FLOW-03b e ainda não aparece.
//
// **Os filtros** são os do S4 (estado, flow, quem chamou, período e conversa, mais a busca pelo id),
// na barra de filtros padrão (`FilterBar`, com o `PeriodPicker` de 7, 30 e 90 dias: o resumo fica 90,
// R13; o padrão é 7). Quem filtra é o servidor: cada mudança sai em `filtrar` com os nomes do pedido
// (`estado`, `flow_nome`, `invocador`, `de` e `ate` em ISO 8601, `conversation_id`, `execucao_id`), e
// o estado da barra é um `v-model:filtros` opcional. O flow se filtra pelo nome, em todas as versões
// (G24), com as opções da lista de Flows da conta (`flows`). "Limpar filtros" não mexe no período,
// como nos relatórios.
//
// O glifo, o nome do estado, quem chamou e os tempos são os das outras telas da aba
// (`useExecucaoNaTela`). O pedido que sai dos filtros (`pedidoDosFiltros`) mora em
// `filtrosDasExecucoes.js`, para a página fazer o primeiro com o mesmo período (Task 6.6).
import { computed, ref, watch } from 'vue';
import { useI18n, I18nT } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import FilterBar from 'dashboard/components-next/filter-bar/FilterBar.vue';
import FilterPill from 'dashboard/components-next/filter-bar/FilterPill.vue';
import PeriodPicker from 'dashboard/components-next/filter-bar/PeriodPicker.vue';
import { humanizar } from '../settings/powerTools/usoDoSegredo';
import { filtrosIniciais, pedidoDosFiltros } from './filtrosDasExecucoes';
import {
  GLIFOS_DOS_ESTADOS,
  ICONES_DE_QUEM,
  useExecucaoNaTela,
} from './useExecucaoNaTela';

const props = defineProps({
  // A página da rota, como veio: `{execucoes: [...], proximo}`.
  lista: { type: Object, default: () => ({ execucoes: [], proximo: null }) },
  // Os nomes dos flows da conta (o `nome` de cada item da lista de Flows), para o filtro de flow.
  flows: { type: Array, default: () => [] },
  carregando: { type: Boolean, default: false },
});

const emit = defineEmits(['abrir', 'filtrar', 'maisAntigas']);

const filtros = defineModel('filtros', {
  type: Object,
  default: filtrosIniciais,
});

const { t } = useI18n();
const { idioma, rotulosDosEstados, quemChamou, haQuanto, duracao } =
  useExecucaoNaTela();

// As colunas, iguais no cabeçalho, nas linhas e no esqueleto: estado, flow, quem chamou, início,
// duração e a seta de abrir.
const GRADE =
  'xl:grid-cols-[8.5rem_minmax(0,1fr)_13rem_7.5rem_5.5rem_1rem] xl:gap-x-4';

// Os nomes das portas no filtro "Quem chamou". O agent-runner tem o dele, para separar do PowerAgents.
const rotulosDeQuem = computed(() => ({
  poweragents: t('POWER_TOOLS.POWERFLOW.EXECUTIONS.INVOKERS.POWERAGENTS'),
  runner: t('POWER_TOOLS.POWERFLOW.EXECUTIONS.INVOKERS.RUNNER'),
  manual: t('POWER_TOOLS.POWERFLOW.EXECUTIONS.INVOKERS.MANUAL'),
  api: t('POWER_TOOLS.POWERFLOW.EXECUTIONS.INVOKERS.API'),
  mcp: t('POWER_TOOLS.POWERFLOW.EXECUTIONS.INVOKERS.MCP'),
  agenda: t('POWER_TOOLS.POWERFLOW.EXECUTIONS.INVOKERS.SCHEDULE'),
  webhook: t('POWER_TOOLS.POWERFLOW.EXECUTIONS.INVOKERS.WEBHOOK'),
}));

// Na falha (erro ou interrompida), o passo onde parou e a mensagem de leigo, quando vêm. O passo vai
// numa linha só no template (como no cofre): quebrado, o `{{ }}` ganha espaço dentro do <code>.
const CLASSE_DO_PASSO =
  'px-1 font-mono rounded bg-n-alpha-black2 text-n-slate-12';
const passoDa = execucao => execucao.passo_onde_parou;
const falhou = execucao =>
  ['erro', 'interrompida'].includes(execucao.estado) &&
  Boolean(execucao.passo_onde_parou || execucao.mensagem?.mensagem);

// -- Tempo
const inicioDa = execucao => execucao.iniciada_em || execucao.criada_em;

const formatoDaData = computed(
  () =>
    new Intl.DateTimeFormat(idioma.value, {
      dateStyle: 'short',
      timeStyle: 'medium',
    })
);
const dataCompleta = instante =>
  instante ? formatoDaData.value.format(new Date(instante)) : '';

// -- Filtros
const execucoes = computed(() => props.lista?.execucoes || []);

// O flow pelo nome, em todas as versões (G24): as opções são os flows da conta, com o título legível.
const opcoesDeFlow = computed(() =>
  props.flows
    .map(nome => ({ id: nome, name: humanizar(nome) }))
    .sort((a, b) => a.name.localeCompare(b.name, idioma.value))
);

const campos = computed(() => [
  {
    key: 'estado',
    label: t('POWER_TOOLS.POWERFLOW.EXECUTIONS.FILTERS.STATE'),
    options: Object.keys(GLIFOS_DOS_ESTADOS).map(estado => ({
      id: estado,
      name: rotulosDosEstados.value[estado],
    })),
    single: true,
    scalar: true,
  },
  {
    key: 'flow_nome',
    label: t('POWER_TOOLS.POWERFLOW.EXECUTIONS.FILTERS.FLOW'),
    options: opcoesDeFlow.value,
    single: true,
    scalar: true,
  },
  {
    key: 'invocador',
    label: t('POWER_TOOLS.POWERFLOW.EXECUTIONS.FILTERS.INVOKER'),
    options: Object.keys(ICONES_DE_QUEM).map(invocador => ({
      id: invocador,
      name: rotulosDeQuem.value[invocador],
    })),
    single: true,
    scalar: true,
  },
  // A conversa é um número, e não uma lista: a pílula abre um campo (o slot abaixo). Fica em
  // "Mais filtros" até ter valor, porque se usa menos.
  {
    key: 'conversation_id',
    label: t('POWER_TOOLS.POWERFLOW.EXECUTIONS.FILTERS.CONVERSATION'),
    scalar: true,
    extra: true,
  },
]);

const naBarra = computed(() => ({
  execucao_id: filtros.value.execucao_id || '',
  estado: filtros.value.estado ?? null,
  flow_nome: filtros.value.flow_nome ?? null,
  invocador: filtros.value.invocador ?? null,
  conversation_id: filtros.value.conversation_id || null,
}));

function mudar(novos) {
  filtros.value = { ...filtros.value, ...novos };
  emit('filtrar', pedidoDosFiltros(filtros.value));
}

const limpar = () =>
  mudar({
    execucao_id: '',
    estado: null,
    flow_nome: null,
    invocador: null,
    conversation_id: null,
  });

// O número da conversa, o que a tela de conversas mostra (o `display_id`), digitado na pílula.
const CLASSE_DO_CAMPO =
  'w-full h-8 min-w-0 px-3 text-sm rounded-lg outline-none reset-base bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:ring-1 focus:ring-n-brand';
const conversaDigitada = ref(String(filtros.value.conversation_id || ''));
watch(
  () => filtros.value.conversation_id,
  numero => {
    conversaDigitada.value = String(numero || '');
  }
);
function aplicarConversa(update, fechar) {
  update([conversaDigitada.value.trim()].filter(Boolean));
  fechar();
}

// Com filtro de pílula ou busca, o vazio é dos filtros; sem, é do período.
const temFiltro = computed(() =>
  Object.values(naBarra.value).some(
    valor => valor !== null && valor !== undefined && valor !== ''
  )
);
</script>

<template>
  <section class="flex flex-col gap-4" data-execucoes-da-conta>
    <header class="flex flex-col gap-1 max-w-3xl">
      <h2 class="mb-0 text-heading-1 text-n-slate-12">
        {{ t('POWER_TOOLS.POWERFLOW.EXECUTIONS.TITLE') }}
      </h2>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('POWER_TOOLS.POWERFLOW.EXECUTIONS.DESCRIPTION') }}
      </p>
    </header>

    <FilterBar
      data-filtros-de-execucoes
      :model-value="naBarra"
      :fields="campos"
      :search="{
        key: 'execucao_id',
        placeholder: t('POWER_TOOLS.POWERFLOW.EXECUTIONS.SEARCH_PLACEHOLDER'),
      }"
      @update:model-value="mudar"
      @reset="limpar"
    >
      <template #period>
        <PeriodPicker
          :model-value="filtros.periodo"
          @update:model-value="periodo => mudar({ periodo })"
        />
      </template>
      <template #pill-conversation_id="{ field, value, update }">
        <!-- A pílula de número da barra (como a do filtro avançado, `FilterConditionPill`): o campo
             abre na lista e vale no "Aplicar", para não pedir a lista a cada tecla. -->
        <FilterPill
          :label="field.label"
          :summary="value[0] || ''"
          :active="value.length > 0"
          data-filtro-de-conversa
          @clear="update([])"
        >
          <template #body="{ close }">
            <form
              class="flex flex-col gap-2 px-2 pb-2"
              @submit.prevent="aplicarConversa(update, close)"
            >
              <input
                v-model="conversaDigitada"
                inputmode="numeric"
                autocomplete="off"
                :class="CLASSE_DO_CAMPO"
                :placeholder="
                  t(
                    'POWER_TOOLS.POWERFLOW.EXECUTIONS.FILTERS.CONVERSATION_PLACEHOLDER'
                  )
                "
                :aria-label="field.label"
              />
              <Button
                type="submit"
                size="sm"
                :label="t('FILTER_BAR.CONDITION.APPLY')"
                :disabled="!conversaDigitada.trim()"
              />
            </form>
          </template>
        </FilterPill>
      </template>
    </FilterBar>

    <!-- Carregando: o esqueleto tem a forma das linhas, e o leitor de tela ouve o texto. -->
    <div
      v-if="carregando"
      role="status"
      class="border divide-y rounded-pz border-n-weak divide-n-weak bg-n-card shadow-pz"
      data-execucoes-carregando
    >
      <span class="sr-only">
        {{ t('POWER_TOOLS.POWERFLOW.EXECUTIONS.LOADING') }}
      </span>
      <div
        v-for="linha in 5"
        :key="linha"
        aria-hidden="true"
        class="flex flex-wrap items-center gap-x-4 gap-y-2 py-4 pe-4 ps-5 motion-safe:animate-pulse xl:grid"
        :class="GRADE"
      >
        <span class="w-20 h-3 rounded bg-n-alpha-2" />
        <span class="flex flex-col flex-1 gap-2">
          <span class="h-3 rounded w-44 bg-n-alpha-2" />
          <span class="h-2.5 rounded w-72 max-w-full bg-n-alpha-2" />
        </span>
        <span class="h-3 rounded w-28 bg-n-alpha-2" />
        <span class="w-16 h-3 rounded bg-n-alpha-2" />
        <span class="w-12 h-3 rounded bg-n-alpha-2" />
        <span />
      </div>
    </div>

    <template v-else-if="execucoes.length">
      <div
        class="overflow-hidden border rounded-pz border-n-weak bg-n-card shadow-pz"
        data-execucoes
      >
        <!-- O cabeçalho das colunas é só para o olho: cada linha já se lê sozinha. -->
        <div
          aria-hidden="true"
          class="hidden py-2.5 pe-4 ps-5 border-b border-n-weak text-xs font-medium text-n-slate-11 xl:grid"
          :class="GRADE"
        >
          <span>{{ t('POWER_TOOLS.POWERFLOW.EXECUTIONS.COLUMNS.STATE') }}</span>
          <span>{{ t('POWER_TOOLS.POWERFLOW.EXECUTIONS.COLUMNS.FLOW') }}</span>
          <span>{{ t('POWER_TOOLS.POWERFLOW.EXECUTIONS.COLUMNS.WHO') }}</span>
          <span>{{
            t('POWER_TOOLS.POWERFLOW.EXECUTIONS.COLUMNS.STARTED')
          }}</span>
          <span>{{
            t('POWER_TOOLS.POWERFLOW.EXECUTIONS.COLUMNS.DURATION')
          }}</span>
        </div>

        <ul class="p-0 m-0 list-none divide-y divide-n-weak">
          <li
            v-for="execucao in execucoes"
            :key="execucao.execucao_id"
            :data-execucao="execucao.execucao_id"
          >
            <button
              type="button"
              class="flex flex-wrap items-center w-full gap-x-4 gap-y-2 py-3 pe-4 ps-5 text-start transition-colors duration-150 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-brand xl:grid"
              :class="GRADE"
              data-abrir-execucao
              @click="emit('abrir', execucao)"
            >
              <span
                class="flex items-center gap-1.5 text-sm text-n-slate-12"
                data-estado
              >
                <span
                  class="size-3.5 shrink-0"
                  :class="GLIFOS_DOS_ESTADOS[execucao.estado]"
                  aria-hidden="true"
                />
                {{ rotulosDosEstados[execucao.estado] }}
              </span>

              <span class="flex flex-col w-full min-w-0 gap-0.5 xl:w-auto">
                <span class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span class="text-sm font-medium truncate text-n-slate-12">
                    {{ humanizar(execucao.flow?.nome) }}
                  </span>
                  <span class="font-mono text-xs text-n-slate-11">
                    {{
                      t('POWER_TOOLS.POWERFLOW.EXECUTIONS.VERSION', {
                        versao: execucao.flow?.versao,
                      })
                    }}
                  </span>
                  <span
                    v-if="execucao.retomadas > 0"
                    class="inline-flex items-center gap-1 text-xs text-n-slate-11"
                    :title="t('POWER_TOOLS.POWERFLOW.EXECUTIONS.RESUMED_HINT')"
                    data-retomada
                  >
                    <span
                      class="i-lucide-history size-3 text-n-slate-10"
                      aria-hidden="true"
                    />
                    {{
                      t(
                        'POWER_TOOLS.POWERFLOW.EXECUTIONS.RESUMED',
                        { count: execucao.retomadas },
                        execucao.retomadas
                      )
                    }}
                  </span>
                </span>
                <span
                  v-if="falhou(execucao)"
                  class="text-xs text-n-slate-11 line-clamp-2"
                  data-onde-parou
                >
                  <I18nT
                    v-if="execucao.passo_onde_parou"
                    keypath="POWER_TOOLS.POWERFLOW.EXECUTIONS.STOPPED_AT"
                    tag="span"
                  >
                    <template #passo>
                      <code :class="CLASSE_DO_PASSO">{{
                        passoDa(execucao)
                      }}</code>
                    </template>
                  </I18nT>
                  <span
                    v-if="
                      execucao.passo_onde_parou && execucao.mensagem?.mensagem
                    "
                    class="inline-block mx-1.5 align-middle rounded-full size-[3px] bg-n-slate-10"
                    aria-hidden="true"
                  />
                  <span>{{ execucao.mensagem?.mensagem }}</span>
                </span>
              </span>

              <span class="flex flex-col min-w-0 gap-0.5" data-quem>
                <span
                  class="flex items-center min-w-0 gap-1.5 text-sm text-n-slate-12"
                >
                  <span
                    class="size-3.5 shrink-0 text-n-slate-10"
                    :class="ICONES_DE_QUEM[execucao.invocador]"
                    aria-hidden="true"
                  />
                  <span class="truncate">{{ quemChamou(execucao) }}</span>
                </span>
                <span
                  v-if="execucao.conversation_id"
                  class="text-xs ps-5 text-n-slate-11"
                >
                  {{
                    t('POWER_TOOLS.POWERFLOW.EXECUTIONS.CONVERSATION', {
                      numero: execucao.conversation_id,
                    })
                  }}
                </span>
              </span>

              <time
                class="text-sm tabular-nums text-n-slate-11"
                :datetime="inicioDa(execucao)"
                :title="dataCompleta(inicioDa(execucao))"
              >
                {{ haQuanto(inicioDa(execucao)) }}
              </time>

              <span class="text-sm tabular-nums text-n-slate-11">
                {{ duracao(execucao.duracao_ms) }}
              </span>

              <span
                class="hidden i-lucide-chevron-right size-4 text-n-slate-10 xl:block"
                aria-hidden="true"
              />
            </button>
          </li>
        </ul>
      </div>

      <div v-if="lista.proximo" class="flex justify-center">
        <Button
          size="sm"
          variant="ghost"
          color="slate"
          icon="i-lucide-chevrons-down"
          data-mais-antigas
          :label="t('POWER_TOOLS.POWERFLOW.EXECUTIONS.LOAD_MORE')"
          @click="emit('maisAntigas')"
        />
      </div>
    </template>

    <div
      v-else
      class="flex flex-col items-center gap-3 px-6 py-12 text-center border rounded-pz border-n-weak bg-n-card shadow-pz"
      data-execucoes-vazio
    >
      <span
        class="grid size-10 place-items-center rounded-xl bg-n-alpha-black2 text-n-slate-11"
      >
        <span
          class="size-4"
          :class="temFiltro ? 'i-lucide-list-filter' : 'i-lucide-activity'"
          aria-hidden="true"
        />
      </span>
      <h3 class="mb-0 text-heading-2 text-n-slate-12">
        {{
          temFiltro
            ? t('POWER_TOOLS.POWERFLOW.EXECUTIONS.FILTERED_EMPTY_TITLE')
            : t('POWER_TOOLS.POWERFLOW.EXECUTIONS.EMPTY_TITLE')
        }}
      </h3>
      <p class="mb-0 text-sm max-w-prose text-n-slate-11">
        {{
          temFiltro
            ? t('POWER_TOOLS.POWERFLOW.EXECUTIONS.FILTERED_EMPTY_DESCRIPTION')
            : t('POWER_TOOLS.POWERFLOW.EXECUTIONS.EMPTY_DESCRIPTION')
        }}
      </p>
    </div>
  </section>
</template>
